import { useEffect, useState, lazy, Suspense } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import { Button, Input, Card, Alert, Loader, Select } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

// Lazily loaded: CodeMirror (+ languages/theme) is a few hundred KB and this page is
// its only consumer — dynamic import keeps it out of the app's initial bundle
// entirely (vite.config.js's manualChunks pins it to its own cacheable chunk).
const CodeEditor = lazy(() => import('@/components/CodeEditor'));

const EMPTY_PARAM = { name: '', type: 'string', description: '', optional: false };
const BLANK_TOOL = {
  name: '', description: '', function_body: 'return input;', timeout_ms: 2000, memory_mb: 32, enabled: false,
  application_id: null,
};

/**
 * Dynamic Tool detail/edit/create page (`/app-dynamic-tool/:id`, or `/new`) — PRD §3.4.
 * The "Test run" section calls the real sandbox directly (POST /api/agent/dynamic-
 * tools/test, services/sandbox/runDynamicTool.js) — the same one an enabled tool and
 * the `test_dynamic_tool` chat tool use, so what an author sees here is exactly what
 * would happen for real, not a simulation.
 */
function paramsToSchema(params) {
  return params.filter((p) => p.name.trim());
}

function ParamRow({ param, onChange, onRemove }) {
  return (
    <div className="flex items-center gap-2">
      <input className="border rounded-md px-2 py-1 text-sm bg-[var(--app-surface)] flex-1" placeholder="name" value={param.name} onChange={(e) => onChange({ ...param, name: e.target.value })} />
      <select className="border rounded-md px-2 py-1 text-sm bg-[var(--app-surface)]" value={param.type} onChange={(e) => onChange({ ...param, type: e.target.value })}>
        <option value="string">string</option>
        <option value="number">number</option>
        <option value="boolean">boolean</option>
      </select>
      <input className="border rounded-md px-2 py-1 text-sm bg-[var(--app-surface)] flex-1" placeholder="description" value={param.description} onChange={(e) => onChange({ ...param, description: e.target.value })} />
      <label className="flex items-center gap-1 text-xs text-secondary-500 whitespace-nowrap">
        <input type="checkbox" checked={!!param.optional} onChange={(e) => onChange({ ...param, optional: e.target.checked })} /> optional
      </label>
      <button type="button" onClick={onRemove} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
    </div>
  );
}

function ParamsSection({ params, setParams }) {
  const update = (i, next) => setParams(params.map((p, idx) => (idx === i ? next : p)));
  const remove = (i) => setParams(params.filter((_, idx) => idx !== i));
  return (
    <details open>
      <summary className="cursor-pointer font-semibold py-2">Parameters ({params.length})</summary>
      <Card className="p-3 space-y-2">
        {params.map((p, i) => <ParamRow key={i} param={p} onChange={(next) => update(i, next)} onRemove={() => remove(i)} />)}
        <button type="button" onClick={() => setParams([...params, { ...EMPTY_PARAM }])} className="flex items-center gap-1 text-xs text-primary-600">
          <Plus size={14} /> Add parameter
        </button>
      </Card>
    </details>
  );
}

function ToolMetaSection({ form, setField, categories, apps }) {
  return (
    <Card className="p-3 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Input label="Name" value={form.name || ''} onChange={(e) => setField('name', e.target.value)} />
        <div className="grid grid-cols-2 gap-2">
          <Input label="Timeout (ms)" type="number" value={form.timeout_ms ?? 2000} onChange={(e) => setField('timeout_ms', e.target.value)} />
          <Input label="Memory (MB)" type="number" value={form.memory_mb ?? 32} onChange={(e) => setField('memory_mb', e.target.value)} />
        </div>
      </div>
      <Input label="Description" value={form.description || ''} onChange={(e) => setField('description', e.target.value)} />
      <div>
        <label className="block text-sm mb-1">Connection category (optional)</label>
        <select
          className="border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)] w-full"
          value={form.connection_category || ''}
          onChange={(e) => setField('connection_category', e.target.value || null)}
        >
          <option value="">None — pure computation</option>
          {categories.map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
        </select>
        <p className="text-xs text-secondary-500 mt-1">
          When set, the run receives the agent's attached connection of this category (decrypted) as the second <code>connection</code> argument.
        </p>
      </div>
      <Select
        label="Application"
        options={[{ label: 'Company-wide (all apps)', value: '' }, ...apps.map(a => ({ label: a.name, value: a.id }))]}
        value={form.application_id || ''}
        onChange={(v) => setField('application_id', v || null)}
        allowClear
        searchable
      />
    </Card>
  );
}

/** Same footprint as the editor it stands in for, so the lazy load never shifts layout. */
function EditorFallback({ height }) {
  return <div style={{ height }} className="border rounded-md bg-[var(--app-surface)] animate-pulse" />;
}

function FunctionBodySection({ form, setField }) {
  return (
    <details open>
      <summary className="cursor-pointer font-semibold py-2">Function body</summary>
      <Card className="p-3">
        <p className="text-xs text-secondary-500 mb-2">
          Body of <code>function(input, connection) {'{'} ... {'}'}</code> — synchronous only, no async/await/fetch/require (isolated-vm, zero capability bridges).
          <code> connection</code> is <code>{'{provider, category, config, credentials}'}</code> when a connection category is set and a matching connection is attached, else <code>null</code>.
        </p>
        <Suspense fallback={<EditorFallback height="220px" />}>
          <CodeEditor
            value={form.function_body || ''}
            onChange={(v) => setField('function_body', v)}
            language="javascript"
            height="220px"
          />
        </Suspense>
      </Card>
    </details>
  );
}

/** Owns its own test-run state, self-contained, so DynamicToolDetail's own hook/state
 *  count doesn't grow every time the test flow needs another field. When the tool has a
 *  connection category, a picker appears: the chosen connection is passed by id only —
 *  the server decrypts it (connections.rotate.org required) and injects it as the
 *  second `connection` argument, exactly like a real agent run would. */
function TestRunSection({ functionBody, timeoutMs, memoryMb, connectionCategory }) {
  const { fetchApi } = useAppApi();
  const [testInput, setTestInput] = useState('{}');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [connections, setConnections] = useState([]);
  const [connectionId, setConnectionId] = useState('');

  useEffect(() => {
    if (!connectionCategory) {
      setConnections([]);
      setConnectionId('');
      return;
    }
    fetchApi('crud', { model: 'AppConnection', action: 'list', where: { category_key: connectionCategory, is_active: true }, limit: 100 })
      .then((res) => setConnections((Array.isArray(res) ? res : res?.data) || []))
      .catch(() => setConnections([])); // connections.view.org may not be granted
  }, [connectionCategory]);

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    let input;
    try {
      input = JSON.parse(testInput || '{}');
    } catch {
      setTestResult({ ok: false, error: 'Sample input is not valid JSON' });
      setTesting(false);
      return;
    }
    try {
      const result = await fetchApi('testDynamicTool', {
        functionBody, input, timeoutMs: Number(timeoutMs) || 2000, memoryMb: Number(memoryMb) || 32,
        ...(connectionId ? { connectionId: Number(connectionId) } : {}),
      });
      setTestResult(result);
    } catch (ex) {
      setTestResult({ ok: false, error: (ex.response && ex.response.data && ex.response.data.error) || ex.message });
    } finally {
      setTesting(false);
    }
  };

  return (
    <details open>
      <summary className="cursor-pointer font-semibold py-2">Test run</summary>
      <Card className="p-3 space-y-3">
        <div>
          <label className="block text-sm mb-1">Sample input (JSON)</label>
          <Suspense fallback={<EditorFallback height="100px" />}>
            <CodeEditor value={testInput} onChange={setTestInput} language="json" height="100px" />
          </Suspense>
        </div>
        {connectionCategory && (
          <div>
            <label className="block text-sm mb-1">Test with connection ({connectionCategory})</label>
            <select
              className="border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)] w-full"
              value={connectionId}
              onChange={(e) => setConnectionId(e.target.value)}
            >
              <option value="">None — connection will be null</option>
              {connections.map((c) => (
                <option key={c.id} value={c.id} disabled={!c.has_credentials}>
                  {c.name}{c.has_credentials ? '' : ' — no credentials stored'}
                </option>
              ))}
            </select>
          </div>
        )}
        <Button size="sm" variant="secondary" onClick={runTest} disabled={testing || !functionBody}>{testing ? 'Running…' : 'Run'}</Button>
        {testResult && (
          <pre className={`text-xs p-3 rounded-md overflow-x-auto ${testResult.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}>
            {JSON.stringify(testResult.ok ? testResult.result : testResult.error, null, 2)}
          </pre>
        )}
      </Card>
    </details>
  );
}

export default function DynamicToolDetail() {
  const { id } = useParams();
  const isNew = id === 'new';
  const nav = useNavigate();
  const { fetchApi } = useAppApi();

  const [form, setForm] = useState(isNew ? { ...BLANK_TOOL } : null);
  const [params, setParams] = useState([]);
  const [categories, setCategories] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    fetchApi('crud', { model: 'AppConnectionCategory', action: 'list', where: { is_active: true }, orderBy: { column: 'sort', direction: 'asc' }, limit: 100 })
      .then((res) => setCategories((Array.isArray(res) ? res : res?.data) || []))
      .catch(() => setCategories([])); // connections.view.org may not be granted
  }, []);

  useEffect(() => {
    fetchApi('applicationsList').then((res) => setApps(Array.isArray(res) ? res : [])).catch(() => []);
  }, []);

  useEffect(() => {
    if (isNew) return;
    fetchApi('crud', { model: 'AppDynamicTool', action: 'show', where: { id } })
      .then((res) => {
        const tool = res && res.data ? res.data : res;
        setForm(tool);
        try { setParams(JSON.parse(tool.input_schema || '[]')); } catch { setParams([]); }
      })
      .catch((ex) => setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message))
      .finally(() => setLoading(false));
  }, [id]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    setSaving(true);
    setErr('');
    const data = {
      name: form.name, description: form.description, function_body: form.function_body,
      input_schema: JSON.stringify(paramsToSchema(params)),
      connection_category: form.connection_category || null,
      timeout_ms: Number(form.timeout_ms) || 2000, memory_mb: Number(form.memory_mb) || 32,
      enabled: !!form.enabled,
      application_id: form.application_id || null,
    };
    try {
      if (isNew) {
        const created = await fetchApi('crud', { model: 'AppDynamicTool', action: 'create', data });
        const newId = (created && created.data ? created.data : created).id;
        nav(`/app-dynamic-tool/${newId}`, { replace: true });
      } else {
        await fetchApi('crud', { model: 'AppDynamicTool', action: 'update', where: { id }, data });
      }
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <PageShell><Loader text="Loading tool…" /></PageShell>;
  if (err && !form) return <PageShell><Alert variant="error" title="Could not load">{err}</Alert></PageShell>;
  if (!form) return null;

  return (
    <PageShell width="narrow" className="space-y-3">
      <PageHeader
        backTo="/app-dynamic-tool"
        backLabel="Dynamic Tools"
        title={isNew ? 'New Dynamic Tool' : form.name}
        actions={(
          <Can module="tools" action="author">
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1 text-sm">
                <input type="checkbox" checked={!!form.enabled} onChange={(e) => setField('enabled', e.target.checked)} /> Enabled
              </label>
              <Button size="sm" onClick={save} disabled={saving || !form.name || !form.function_body}>{saving ? 'Saving…' : 'Save'}</Button>
            </div>
          </Can>
        )}
      />

      {err && <Alert variant="error" title="Error">{err}</Alert>}

      <ToolMetaSection form={form} setField={setField} categories={categories} apps={apps} />
      <FunctionBodySection form={form} setField={setField} />
      <ParamsSection params={params} setParams={setParams} />
      <TestRunSection functionBody={form.function_body} timeoutMs={form.timeout_ms} memoryMb={form.memory_mb} connectionCategory={form.connection_category} />
    </PageShell>
  );
}
